"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";

type Category = "all" | "membership" | "business";

type ServiceCard = {
  title: string;
  description: string;
  href: string;
  imageSrc: string;
  category: "membership" | "business";
};

type Tab = {
  key: Category;
  label: string;
};

export default function FilteredServices({
  tabs,
  cards,
  learnMoreLabel,
}: {
  tabs: Tab[];
  cards: ServiceCard[];
  learnMoreLabel: string;
}) {
  const [active, setActive] = useState<Category>("all");

  const filtered =
    active === "all" ? cards : cards.filter((c) => c.category === active);

  return (
    <section className="bg-[#0b0b0d] py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Tabs */}
        <div className="flex gap-3 mb-10 flex-wrap">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActive(tab.key)}
              className={`rounded-full px-5 py-2 text-[11px] uppercase transition-colors duration-200 cursor-pointer ${
                active === tab.key
                  ? "bg-[#9B7755] text-white border border-[#9B7755]"
                  : "border border-white/25 text-white/60 hover:border-white/50 hover:text-white/80"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {filtered.map((card) => (
            <article key={card.title} className="flex flex-col">
              <div className="relative w-full aspect-square rounded-2xl overflow-hidden">
                <Image
                  src={card.imageSrc}
                  alt={card.title}
                  fill
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 33vw"
                />
              </div>
              <div className="mt-5">
                <h3 className="text-sm uppercase text-white font-normal font-body">
                  {card.title}
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-white/50 font-body">
                  {card.description}
                </p>
                <div className="mt-5">
                  <Link
                    href={card.href}
                    className="inline-flex items-center rounded-full border border-white/25 px-5 py-2 text-[10px] tracking-[0.2em] uppercase text-white/70 hover:border-white hover:text-white transition-colors duration-200"
                  >
                    {learnMoreLabel}
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
